"use strict";
import Map from "ol/Map";
import { eventBus } from "@/services/utils";
import View from "ol/View";
import {
  Group as LayerGroup,
  Tile as TileLayer,
  Vector as VectorLayer,
  VectorImage as VectorImageLayer
} from "ol/layer";
import {ImageArcGISRest, ImageWMS, Vector as VectorSource, XYZ as XYZSource} from "ol/source";
import ImageLayer from "ol/layer/Image";
import Overlay from "ol/Overlay";
import Feature from "ol/Feature";
import { Fill, Circle as CircleStyle, Stroke, Style } from "ol/style.js";
import Point from "ol/geom/Point";
import Circle from "ol/geom/Circle";
import { fromLonLat } from "ol/proj";
import { defaults as defaultControls, ScaleLine } from "ol/control";
import {
  BasemapGallery,
  Geolocation,
  InitialExtent,
  Search,
  Coordinates,
  Swipe,
  Measurement,
  Export,
  Identify,
  Legend,
} from "chiren";
import { logo } from "./resources";
import { tile as tileStrategy, bbox as bboxStrategy } from 'ol/loadingstrategy'
import { createXYZ } from 'ol/tilegrid'
import { createStyleFunction } from '@/services/esri-style-parser'
import GeoJSON from "ol/format/GeoJSON";
import axios from 'axios'

let map, swipeTool, exportTool, measurementTool, identifyTool, legendTool;
let clickToDrawTooltip;
let pseudoIdentifyOverlay;
let vectorSource;

const styles = {
  point: new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: "#4CA1F7" }),
      stroke: new Stroke({ color: "white", width: 2 }),
    }),
    zIndex: 100,
  }),
  buffer: new Style({
    fill: new Fill({
      color: "rgba(76, 161, 247, 0.55)",
    }),
  }),
};

function createLayer(l, token) {
  const config = l.olConfig || {}
  token = config?.skipAuth ? null : token
  const zoom = config?.zoom
  // Create category
  if (l.category) {
    return new LayerGroup({
      exclude: false,
      name: l.name,
      type: l.type,
      layers: l.layers.map((sl) => createLayer(sl, token)),
    });
  } else {
    switch (l.loader) {
      case 'ArcGis-Map':
        return new ImageLayer({
          name: l.name,
          type: l.type,
          servername: l.servername,
          visible: l.visible,
          opacity: 0.85,
          description: l.description,
          // identifyFormat: l.sourceConfig.custom.identifyFormat,
          minZoom: zoom,
          whiteList: l.olConfig ? l.olConfig.whiteList : null,
          source: new ImageArcGISRest({
            url: l.url,
            ratio: 1,
            hidpi: false,
            crossOrigin: "anonymous",
            params: {
              token: token,
              layers: `show:${config.layers.join(',')}`
            }
          }),
          zIndex: config.zIndex || 1
        });
      case 'ArcGis-Feature':
        const formData = new FormData()
        const source  = new VectorSource({
          url: l.url,
          // timing: config?.Timeconfig,
          async loader (extent, resolution, projection) {
            const maxAllowableOffset = resolution * 2
            let res
            const url = `${l.url}/query`
            // common data
            const formData = new FormData()
            formData.append('f', 'geojson')
            formData.append('returnGeometry', 'true')
            formData.append('outFields', '*')
            formData.append('spatialRel', 'esriSpatialRelIntersects')
            formData.append('maxAllowableOffset', maxAllowableOffset)
            formData.append('resultType', 'tile')
            formData.append('outSR', '102100')
            formData.append('maxRecordCountFactor', '3')
            formData.append('returnExceededLimitFeatures', 'true')
            formData.append('returnCentroid', 'false')

            // apply custom geometry filtering
            if (l.olConfig?.geometryFilter) {
              formData.append('geometryType', 'esriGeometryPolygon')
              formData.append('inSR', '4326')
              formData.append('geometry', JSON.stringify(config.geometryFilter))

              res = await fetch(url, { method: 'post', body: formData })
            } else {
              // apply default view map extent
              formData.append('geometryType', 'esriGeometryEnvelope')
              formData.append('inSR', '102100')
              formData.append('geometry',
                  '{"xmin":' +
                  extent[0] +
                  ',"ymin":' +
                  extent[1] +
                  ',"xmax":' +
                  extent[2] +
                  ',"ymax":' +
                  extent[3] +
                  ',"spatialReference":{"wkid":102100,"latestWkid":3857}}'
              )
            }

            if (l.olConfig?.isAuth) {
              formData.append('token', token)
            }

            if (l.olConfig?.query) formData.append('where', l.olConfig.query)

            res = await fetch(url, { method: 'post', body: formData })

            const json = await res.json()
            const features = new GeoJSON().readFeatures(json, { featureProjection: 'EPSG:3857' })
            this.addFeatures(features)
          },
          strategy: tileStrategy(
              createXYZ({
                tileSize: 512
              })
          )
        })
        const olLayer = new VectorImageLayer({
          name: l.name,
          type: l.type,
          id: l.id,
          identify: 'VectorArcGIS',
          token: token,
          // minResolution: l.ScaleMax ? l.ScaleMax / ARCGIS_SCALE_OL_RESOLUTION_TRANSFORM / (METERS_PER_UNIT[this.units = map.getView().getProjection().getUnits()] * PPI) : undefined,
          // maxResolution: l.ScaleMin ? l.ScaleMin / ARCGIS_SCALE_OL_RESOLUTION_TRANSFORM / (METERS_PER_UNIT[this.units = map.getView().getProjection().getUnits()] * PPI) : undefined,
          // minZoom: config?.minZoom || undefined,
          // maxZoom: config?.maxZoom || undefined,
          visible: l.deferEnable ? false : (l.Enabled || false),
          // opacity: getOpacity(l.Opacity),
          source,
          zIndex: config.zIndex || 1
        })
        if (l.Private && !token) {
          // layer is Private and we have no token => we cannot style now
        } else {
          setArcGISFeatureStyles(l, l.sourceConfig?.isAuth ? token : null, olLayer, config)
        }
        return olLayer
      case 'WMS':
        return new ImageLayer({
          name: l.name,
          type: l.type,
          servername: l.servername,
          visible: l.visible,
          opacity: 0.85,
          description: l.description,
          // identifyFormat: l.sourceConfig?.custom.identifyFormat,
          whiteList: l.olConfig ? l.olConfig.whiteList : null,
          legendSize: l.olConfig?.legendSize || null,
          zIndex: config.zIndex || 1,
          source: new ImageWMS({
            url: l.url,
            params: l.sourceConfig.params,
            serverType: "geoserver",
            crossOrigin: "Annonymous",
          }),

        });
    }
  }
}

export function initialize(selector, mapLayers, token) {
  vectorSource = new VectorSource({ wrapX: false });
  let layers = [
    new TileLayer({
      exclude: true,
      source: new XYZSource({
        crossOrigin: "Anonymous",
        attributions:
          'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/' +
          'rest/services/World_Topo_Map/MapServer">ArcGIS</a>',
        url:
          "https://server.arcgisonline.com/ArcGIS/rest/services/" +
          "World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
      }),
    }),
  ].concat(
    new VectorLayer({
      source: vectorSource,
      exclude: true,
      zIndex: 200
    })
  );
  mapLayers.forEach((item) => {
    if (item.category) {
      item.layers = mapLayers.filter((l) => l["layer-parent-id"] === item.id);
    }
    if (!item["layer-parent-id"])
      layers.push(createLayer(item, token));
  });
  // console.log(layers)
  map = new Map({
    controls: defaultControls({
      attribution: false,
    }).extend([
      new BasemapGallery({
        control: true,
        basemaps: require("./basemaps").default,
      }),
      new Geolocation(),
      new InitialExtent(),
      new Search({
        fixed: true,
        control: true,
        primary: "#004F27",
        accent: "#ff0000",
        geocoder: "locationiq",
        geocoderOpts: {
          country: "us",
          key:
            process.env.VUE_APP_LOCATION_IQ ||
            "pk.d7437e672f63ca549e557bcd64dfe183",
          polygon: false,
        },
      }),
      new ScaleLine({ units: "us" }),
      new Coordinates({ primary: "#676767", accent: "#004F27" }),
    ]),
    target: selector,
    layers: layers,
    view: new View({
      // projection: 'EPSG:4326',
      // projection: 'EPSG:26913',
      center: fromLonLat([-105.57309, 38.9916]),
      zoom: 7,
    }),
  });
  swipeTool = new Swipe({ control: false });
  exportTool = new Export({
    control: false,
    pdfServiceUrl: "https://report.technosylva.com/generate",
  });
  identifyTool = new Identify({ control: false });
  legendTool = new Legend({ control: false });
  measurementTool = new Measurement({
    control: false,
    primary: "#004F27",
    secondary: "#004F27",
    accent: "#DEC68A",
    whitedot: true,
    fill: "rgba(0,0,0, 0.2)",
    defaultUnits: { distance: "Imperial (Auto)", area: "Imperial (Auto)" },
  });

  map.addControl(swipeTool);
  map.addControl(exportTool);
  map.addControl(identifyTool);
  map.addControl(measurementTool);
  map.addControl(legendTool);
  eventBus.$emit("map-created");

  clickToDrawTooltip = new Overlay({
    element: document.getElementById("clickToDraw"),
    autoPan: false,
    autoPanAnimation: {
      duration: 250,
    },
  });

  pseudoIdentifyOverlay = new Overlay({
    element: document.getElementById("pseudoIdentifyOverlay"),
    autoPan: false,
    positioning: "bottom-center",
    autoPanAnimation: {
      duration: 250,
    },
  });

  map.addOverlay(pseudoIdentifyOverlay);
}

export function flyToLocation(coordinates, zoom) {
  map
    .getView()
    .animate({ center: fromLonLat([coordinates.lon, coordinates.lat]), zoom });
}

export function drawPoint(coordinates) {
  clearPoints();
  const point = new Feature({
    geometry: new Point(coordinates),
  });
  const buffer = new Feature({
    geometry: new Circle(coordinates, 500),
  });
  point.setStyle(styles.point);
  buffer.setStyle(styles.buffer);
  vectorSource.addFeature(buffer);
  vectorSource.addFeature(point);
}

export function clearPoints() {
  vectorSource.clear();
}

export function zoomToLonLat(lonLat, zoomlevel) {
  return flyToLocation(
    { lon: parseFloat(lonLat[0]), lat: parseFloat(lonLat[1]) },
    zoomlevel | 10
  );
}

export function enableClickToDrawTooltip() {
  map.addOverlay(clickToDrawTooltip);
  map.on("pointermove", onPointerMove);
}

export function disableClickToDrawTooltip() {
  map.removeOverlay(clickToDrawTooltip);
  map.un("pointermove", onPointerMove);
}

function onPointerMove(evt) {
  const coordinate = evt.coordinate;
  clickToDrawTooltip.setPosition(coordinate);
}

export function setOpacity(opacity, layerList) {
  const layers = map.getLayers();
  layers.forEach((l, i) => {
    if (i === 0) return;
    if (!layerList || (layerList && layerList.indexOf(l.get("name")) !== -1))
      l.setOpacity(opacity);
  });
}

export function toggleLayer(name) {
  const layer = map
    .getLayers()
    .getArray()
    .find((l) => l.get("name") === name);
  if (!layer) return;
  const visible = layer.getVisible();
  if (visible) layer.setVisible(false);
  else layer.setVisible(true);
}

export function toggleSwipe() {
  swipeTool.toggleSwipeTool();
}

export function toggleMeasurement() {
  measurementTool.toggleMeasurementTool();
}

export function toggleIdentify() {
  identifyTool.toggleIdentifyTool();
}

export async function printMap(title = "", description = "", theme = "") {
  const jobId = await exportTool.toPdf("/generic/print", {
    title,
    description,
    theme,
    client: "Colorado Wildfire Risk Viewer",
    timezone: "America/Denver",
    url: "https://co-pub.coloradoforestatlas.org",
    legend: await legendTool.toJSON(),
    linkColor: "#004f27",
    filename: {
      value: `cowrap_{timestamp}`,
      opts: {
        dateformat: "yyyyMMdd_Hmmssa",
      },
    },
    logo,
    disclaimer: {
      header: "Disclaimer",
      p: [
        "The user assumes the entire risk related to their use of the Colorado Wildfire Risk Viewer and either the published or derived products from these data.",
        "The Colorado State Forest Service is providing these data “as is” and disclaims any and all warranties, whether expressed or implied, including (without limitation) any implied warranties of merchantability or fitness for a particular purpose.",
        "In no event will Colorado State Forest Service be liable to you or to any third party for any direct, indirect, incidental, consequential, special or exemplary damages or lost profit resulting from any use or misuse of these data.",
      ],
    },
  });
  if (jobId === 1) throw Error(jobId);
  return `https://report.technosylva.com/files/${jobId}.pdf`;
}

export function toggleLegend() {
  legendTool.toggleOmniLegend();
}

export function setLayerVisibility(name, visible, category) {
  let layer = null;
  if (category) {
    category = map
      .getLayers()
      .getArray()
      .find((c) => c.get("name") === category);
    layer = category
      .getLayers()
      .getArray()
      .find((l) => l.get("name") === name);
  } else {
    layer = map
      .getLayers()
      .getArray()
      .find((l) => l.get("name") === name);
  }
  if (!layer) return;
  layer.setVisible(visible);
}

export function getMap() {
  return map;
}

export function getPseudoIdentifyOverlay() {
  return pseudoIdentifyOverlay;
}

function setArcGISFeatureStyles (layer, token, olLayer, config) {
  /// if (layer.DisplayName === 'Thermal Hotspots and Fire Activity') config = { premadeStyle: 'thermal-hotspots' }
  if (config?.premadeStyle) {
    const styleFunc = require('./premade-styles/' + config.premadeStyle).default
    olLayer.setStyle(styleFunc)
  } else {
    axios.get(`${layer.url}?f=pjson${token ? `&token=${token}` : ''}`)
        .then(async (res) => {
          try {
            olLayer.set('drawingInfo', res.data.drawingInfo)
            const styleFunc = await createStyleFunction(res.data, config)
            olLayer.setStyle(styleFunc)
          } catch (error) {
            console.warn('Cannot generate ESRI style: ', layer.DisplayName) // eslint-disable-line
            console.warn(error) // eslint-disable-line
          }
        })
  }
}

export async function generateToken () {
  const data = {
    'client': 'referer',
    'expiration': '180',
    'username': 'co_fuelsviewer',
    'password': 'It$lrR6k8O6A',
    'referer': window.location.origin,
    'f': 'json'
  }
  const config = {
    method: 'post',
    url: 'https://gisus.technosylva.com/arcgis/tokens/generateToken',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data : data
  };

  try {
    const res = await axios(config)
    return res.data.token
  } catch (error) {
    console.error('Error while generating token')
  }
}
